import { useEffect, useState } from "react";
import {
  AvailableUpgrade,
  Currency,
  SubscriptionUpgradeType,
  UpgradeOptionsResponse,
  fetchSubscriptionUpgradeOptions,
  mutationCreateSubscriptionIntent,
  mutationSubscriptionUpgrade,
  stripeAmountToMajor,
} from "@jammable/clientapi/src/payments";
import { toast } from "@jammable/ui-core";
import { getCurrencySymbol } from "../utils";

export interface UseUpgradeOptionsProps {
  type?: SubscriptionUpgradeType;
  interval?: "month" | "year";
  fallback?: () => void;
  callback?: () => void;
  from?: string;
}

export function useUpgradeOptions({
  type,
  interval = "month",
  callback,
  fallback,
  from,
}: UseUpgradeOptionsProps) {
  const [upgradeOptions, setUpgradeOptions] =
    useState<UpgradeOptionsResponse>();
  const [isUpgrading, setIsUpgrading] = useState<boolean>(false);
  const [selectedPlan, setSelectedPlan] = useState<AvailableUpgrade>();

  const [showCheckout, setShowChekout] = useState(false);
  const [checkoutParams, setcheckoutParams] = useState<{
    clientSecret: string;
    priceId: string;
    subType: string;
    currency: Currency;
    symbol: string;
    price: number;
  }>();

  const currency =
    upgradeOptions?.subscription?.currency ||
    upgradeOptions?.availableUpgrades[0]?.currency;

  const handleSelectPlan = (plan: AvailableUpgrade) => {
    setSelectedPlan(plan);
  };

  useEffect(() => {
    const fetchUpgradeOptions = async () => {
      try {
        setUpgradeOptions(await fetchSubscriptionUpgradeOptions());
      } catch (e) {
        fallback?.();
      }
    };
    fetchUpgradeOptions();
  }, [fallback]);

  const handleUpgrade = async () => {
    try {
      setIsUpgrading(true);
      await mutationSubscriptionUpgrade(
        type,
        undefined,
        undefined,
        `upgrade-options${from ? `:${from}` : ""}`,
      );
      callback?.();
    } catch (e) {
      fallback?.();
    }
  };

  const handleNewSubscription = async () => {
    try {
      setIsUpgrading(true);
      const intent = await mutationCreateSubscriptionIntent({
        priceId: selectedPlan!.id,
        currency: currency!.toUpperCase() as Currency,
        from: from,
        couponId: selectedPlan?.selectedCoupon?.id,
      });
      setcheckoutParams({
        clientSecret: intent.clientSecret,
        priceId: selectedPlan!.id,
        subType: selectedPlan!.type,
        currency: currency!.toUpperCase() as Currency,
        symbol: getCurrencySymbol(currency!.toUpperCase() as Currency),
        price: stripeAmountToMajor(
          selectedPlan!.discountedAmount && selectedPlan!.discountedAmount > 0
            ? selectedPlan!.discountedAmount
            : selectedPlan!.amount,
          currency!,
        ),
      });
      setShowChekout(true);
    } catch (e) {
      setIsUpgrading(false);
      toast.error("Something went wrong, please try again later");
      fallback?.();
    }
  };

  const upgrade: AvailableUpgrade | null = type
    ? upgradeOptions?.availableUpgrades?.find(
        (option) =>
          option.product?.name === type && option.plan?.interval === interval,
      ) || null
    : null;

  return {
    isLoadingPrices: !upgradeOptions,
    isUpgrading,
    currency,
    promoApplies: upgradeOptions?.promoApplies,
    upgradeOptions: upgradeOptions?.availableUpgrades,
    upgradeOfType: upgrade,
    selectedPlan,
    selectedPlanName: selectedPlan?.product.name,
    handleSelectPlan,
    handleUpgrade,
    handleNewSubscription,
    showCheckout,
    checkoutParams,
  };
}
